import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ArticleCarousel from '../components/ArticleCarousel';
import parse from 'html-react-parser';

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=800&q=80';

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableOfContents, setTableOfContents] = useState<TableOfContentsItem[]>([]);
  const [isTocOpen, setIsTocOpen] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setIsLoading(true);
      setError(null);

      if (!slug || slug === 'undefined' || slug === 'null') {
        navigate('/resources', { replace: true });
        return;
      }

      try {
        const { data, error: supabaseError } = await supabase
          .from('articles')
          .select('id, title, content, description, image_url, category, created_at, slug')
          .eq('slug', slug)
          .maybeSingle();

        if (supabaseError) {
          throw new Error(`Error fetching article: ${supabaseError.message}`);
        }
        
        if (!data) {
          navigate('/resources', { replace: true });
          return;
        }

        const { processedContent, toc } = processContent(data.content);
        
        setArticle({
          ...data,
          content: processedContent
        });
        setTableOfContents(toc);
      } catch (error: any) {
        console.error('Error fetching article:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [slug, navigate]);

  const processContent = (content: string): { processedContent: string, toc: TableOfContentsItem[] } => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const toc: TableOfContentsItem[] = [];
    
    // Find all headings
    const headings = doc.querySelectorAll('h1, h2, h3');
    
    headings.forEach((heading, index) => {
      // Create unique ID based on heading text
      const text = heading.textContent || '';
      const id = `heading-${index}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      
      // Add ID to heading
      heading.id = id;
      
      // Add to table of contents
      toc.push({
        id,
        text,
        level: parseInt(heading.tagName.substring(1))
      });
    });

    return {
      processedContent: doc.body.innerHTML,
      toc
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p>{error || 'Article not found'}</p>
          </div>
          <Link to="/resources" className="mt-4 text-indigo-600 hover:text-indigo-800 inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Featured Image Header */}
      <div className="w-full h-[50vh] relative bg-gray-900">
        <img
          src={article.image_url || DEFAULT_IMAGE}
          alt={article.title}
          className="w-full h-full object-cover opacity-90"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== DEFAULT_IMAGE) {
              target.src = DEFAULT_IMAGE;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white max-w-7xl mx-auto">
          <div className="mb-4">
            <Link 
              to="/resources" 
              className="text-white/80 hover:text-white inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to resources
            </Link>
          </div>
          <span className="inline-block bg-blue-500 text-white text-sm px-3 py-1 rounded-full mb-4">
            {article.category}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {article.title}
          </h1>
          <div className="flex items-center text-white/80">
            <Calendar className="w-5 h-5 mr-2" />
            <span>
              {new Date(article.created_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Article Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <article className="w-full lg:w-[70%] bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-8">
              {article.description && (
                <p className="text-xl text-gray-600 mb-8 font-medium">
                  {article.description}
                </p>
              )}
              
              <div className="prose prose-lg max-w-none mx-auto">
                {parse(article.content)}
              </div>
            </div>
          </article>

          {/* Sidebar with Table of Contents */}
          {tableOfContents.length > 0 && (
            <aside className="w-full lg:w-[30%] lg:block">
              <div className="sticky top-24 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setIsTocOpen(!isTocOpen)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h2 className="text-lg font-semibold text-gray-800">Sommaire</h2>
                  {isTocOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isTocOpen ? 'max-h-[calc(100vh-200px)] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <nav className="p-4 overflow-y-auto">
                    <ul className="space-y-2">
                      {tableOfContents.map((item, index) => (
                        <li 
                          key={index}
                          className={`${
                            item.level === 1 ? 'ml-0' :
                            item.level === 2 ? 'ml-4' :
                            'ml-8'
                          }`}
                        >
                          <a
                            href={`#${item.id}`}
                            className="text-gray-600 hover:text-indigo-600 transition-colors block py-1"
                            onClick={(e) => {
                              e.preventDefault();
                              const element = document.getElementById(item.id);
                              if (element) {
                                const yOffset = -100;
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                              }
                            }}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            </aside>
          )}
        </div>

        {/* Recent Articles Carousel */}
        <div className="mt-12">
          <ArticleCarousel currentArticleId={article.id} />
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;